import {
  CorporateInformationEnglishFormValues,
  CorporateInformationThaiFormValues,
  MarketingIndustryFormValues,
  MarketingPolicyFormValues,
  ProductInformationFormValues,
} from './Containers/NatureOfBusinessFormProvider/types';
import {
  IntroductionFormValues,
  RemarkFormValues,
} from '@Utils/Types/FormValuesTypes';
import {
  checkAssetsValidEn,
  checkAssetsValidTh,
} from '@Containers/UploadImageFormFields/utils';
import {
  checkMessageEn,
  validateSpace,
  validateSpaceTextInput,
} from '@Utils/validation';
import {
  checkValidIntroductionEn,
  checkValidRemarkEn,
} from '@Utils/validationIntroRemark';

export const enabledGeneralInfoCompletedSection = (
  generalInfo:
    | CorporateInformationThaiFormValues
    | CorporateInformationEnglishFormValues
    | IntroductionFormValues
    | RemarkFormValues
): boolean => {
  if (!generalInfo) return false;

  let isAllFieldsFilled = true;

  const validateEnglishFields = [
    'introductionEn',
    'remarkEn',
    'addressEn',
    'businessTypeEn',
    'companyEn',
  ];

  const validateNumberFields = ['zipcodeTh', 'zipcodeEn'];

  const validateTextAreaSpaceFields = [
    'introductionEn',
    'introductionTh',
    'companyEn',
    'companyTh',
    'addressEn',
    'addressTh',
    'businessTypeEn',
    'businessTypeTh',
  ];

  const validateTextInputSpaceFields = [
    'remarkEn',
    'remarkTh',
    'symbolEn',
    'symbolTh',
    'registeredNumberTh',
    'registeredNumberEn',
    'faxEn',
    'faxTh',
    'telephoneEn',
    'telephoneTh',
    'emailEn',
    'emailTh',
  ];

  const optionalFields = [
    'completeSection.isCompleteTh',
    'completeSection.isCompleteEn',
    'faxTh',
    'faxEn',
    'introductionEn',
    'introductionTh',
    'remarkEn',
    'remarkTh',
  ];

  const checkFields = (obj: Record<string, any>) => {
    Object.entries(obj).forEach(([key, value]) => {
      if (
        validateNumberFields.includes(key) &&
        typeof value === 'string' &&
        !/^\d+$/.test(value)
      ) {
        isAllFieldsFilled = false;
        return false;
      }

      if (
        validateEnglishFields.includes(key) &&
        typeof value === 'string' &&
        !checkMessageEn(value)
      ) {
        isAllFieldsFilled = false;
        return;
      }

      if (validateTextAreaSpaceFields.includes(key)) {
        if (typeof value === 'string' && !validateSpace(value)) {
          isAllFieldsFilled = false;
          return;
        }
      }

      if (validateTextInputSpaceFields.includes(key)) {
        if (typeof value === 'string' && !validateSpaceTextInput(value)) {
          isAllFieldsFilled = false;
          return;
        }
      }

      if (optionalFields.includes(key)) {
        return;
      }

      if (value && typeof value === 'object') {
        checkFields(value);
      } else if (value === '' || value === null || value === undefined) {
        isAllFieldsFilled = false;
      }
    });
  };

  checkFields(generalInfo);

  return isAllFieldsFilled;
};

export const enabledProductInfoCompletedSectionTh = (
  productInformation: ProductInformationFormValues
) => {
  if (!productInformation || !productInformation.businessType) {
    return false;
  }

  const {
    introduction,
    remark,
    businessGroupIntroduction,
    businessGroupRemark,
    businessGroupInfos,
  } = productInformation;

  const isIntroSpaceValid = validateSpace(introduction?.descriptionTh);
  const isRemarkSpaceValid = validateSpaceTextInput(remark?.descriptionTh);

  const isBusinessGroupIntroSpaceValid = validateSpace(
    businessGroupIntroduction?.descriptionTh
  );
  const isBusinessGroupRemarkSpaceValid = validateSpaceTextInput(
    businessGroupRemark?.descriptionTh
  );

  const isValidBusinessGroupInfo = businessGroupInfos.every((businessGroup) => {
    const { descriptionTh, nameTh, assets } = businessGroup;

    const isValidDescriptionAndNameTh = descriptionTh && nameTh;
    const isDescriptionSpaceValid = validateSpace(descriptionTh);
    const isNameSpaceValid = validateSpaceTextInput(nameTh);
    const isValidAssets = checkAssetsValidTh({ assets });

    return (
      isValidDescriptionAndNameTh &&
      isValidAssets &&
      isDescriptionSpaceValid &&
      isNameSpaceValid
    );
  });

  return (
    isValidBusinessGroupInfo &&
    isIntroSpaceValid &&
    isRemarkSpaceValid &&
    isBusinessGroupIntroSpaceValid &&
    isBusinessGroupRemarkSpaceValid
  );
};

export const enabledProductInfoCompletedSectionEn = (
  productInformation: ProductInformationFormValues
): boolean => {
  if (!productInformation || !productInformation.businessType) {
    return false;
  }

  const {
    introduction,
    remark,
    businessGroupIntroduction,
    businessGroupRemark,
    businessGroupInfos,
  } = productInformation;

  const isIntroductionValid = checkValidIntroductionEn(introduction);
  const isRemarkValid = checkValidRemarkEn(remark);

  const isBusinessGroupIntroductionValid = checkValidIntroductionEn(
    businessGroupIntroduction
  );
  const isBusinessGroupRemarkValid = checkValidRemarkEn(businessGroupRemark);

  const areBusinessGroupInfosValid = businessGroupInfos.every(
    (businessGroup) => {
      const { descriptionEn, nameEn, assets } = businessGroup;

      const isValidEnglishDescription =
        !!descriptionEn && checkMessageEn(descriptionEn);
      const isValidEnglishName = !!nameEn && checkMessageEn(nameEn);

      const isValidEnglish = isValidEnglishDescription && isValidEnglishName;

      const isValidAssets = checkAssetsValidEn({ assets });

      return isValidAssets && isValidEnglish;
    }
  );

  return (
    isIntroductionValid &&
    isRemarkValid &&
    isBusinessGroupIntroductionValid &&
    isBusinessGroupRemarkValid &&
    areBusinessGroupInfosValid
  );
};

export const enabledMarketingIndustryCompletedSectionTh = (
  marketingPolicy?: MarketingPolicyFormValues[],
  marketingIndustry?: MarketingIndustryFormValues
) => {
  if (!marketingPolicy || marketingPolicy.length === 0) return false;
  let isAllThFilled = true;

  const {
    introduction,
    remark,
    marketingPolicyIntroduction,
    marketingPolicyRemark,
  } = marketingIndustry || {};

  if (!validateSpace(introduction?.descriptionTh)) {
    isAllThFilled = false;
  }

  if (!validateSpaceTextInput(remark?.descriptionTh)) {
    isAllThFilled = false;
  }

  if (!validateSpace(marketingPolicyIntroduction?.descriptionTh)) {
    isAllThFilled = false;
  }

  if (!validateSpaceTextInput(marketingPolicyRemark?.descriptionTh)) {
    isAllThFilled = false;
  }

  marketingPolicy.forEach((item) => {
    const {
      descriptionTh,
      industryCompetitionDescTh,
      salesRatioIntroduction,
      salesRatioRemark,
      assets,
    } = item;

    if (!descriptionTh?.trim()) {
      isAllThFilled = false;
    }
    if (!industryCompetitionDescTh?.trim()) {
      isAllThFilled = false;
    }

    if (!validateSpace(salesRatioIntroduction?.descriptionTh)) {
      isAllThFilled = false;
    }

    if (!validateSpaceTextInput(salesRatioRemark?.descriptionTh)) {
      isAllThFilled = false;
    }

    if (!validateSpace(descriptionTh)) {
      isAllThFilled = false;
    }
    if (!validateSpace(industryCompetitionDescTh)) {
      isAllThFilled = false;
    }

    if (assets?.length) {
      const isValidAssetsTh = checkAssetsValidTh(item);

      isAllThFilled = isValidAssetsTh;
    }
  });

  return isAllThFilled;
};

export const enabledMarketingIndustryCompletedSectionEn = (
  marketingPolicy?: MarketingPolicyFormValues[],
  marketingIndustry?: MarketingIndustryFormValues
) => {
  if (!marketingPolicy?.length) return false;

  const validateEnglishFields = (policy: MarketingPolicyFormValues) => {
    const {
      descriptionEn,
      industryCompetitionDescEn,
      salesRatioIntroduction,
      salesRatioRemark,
    } = policy;

    const isDescriptionValid =
      descriptionEn &&
      checkMessageEn(descriptionEn) &&
      validateSpace(descriptionEn);

    const isIndustryCompetitionValid =
      industryCompetitionDescEn &&
      checkMessageEn(industryCompetitionDescEn) &&
      validateSpace(industryCompetitionDescEn);

    const isSalesRatioIntroductionValid = checkValidIntroductionEn(
      salesRatioIntroduction
    );
    const isSalesRatioRemarkValid = checkValidRemarkEn(salesRatioRemark);

    const isMarketingIndustryIntroductionValid = checkValidIntroductionEn(
      marketingIndustry?.introduction
    );
    const isMarketingIndustryRemarkValid = checkValidRemarkEn(
      marketingIndustry?.remark
    );

    const isValidAssetsEn = checkAssetsValidEn(policy);

    const isMarketingPolicyIntroductionValid = checkValidIntroductionEn(
      marketingIndustry?.marketingPolicyIntroduction
    );
    const isMarketingPolicyRemarkValid = checkValidRemarkEn(
      marketingIndustry?.marketingPolicyRemark
    );

    return (
      isDescriptionValid &&
      isIndustryCompetitionValid &&
      isSalesRatioIntroductionValid &&
      isSalesRatioRemarkValid &&
      isMarketingIndustryIntroductionValid &&
      isMarketingIndustryRemarkValid &&
      isMarketingPolicyIntroductionValid &&
      isMarketingPolicyRemarkValid &&
      isValidAssetsEn
    );
  };

  return marketingPolicy.every((policy) => validateEnglishFields(policy));
};
